import React from "react";

import DataTable from "../../../DataTable";

import useOutputReportStore from "../../../../store/OutputReport.store";
import {
  formatDataTableColumns,
  getOutputType,
} from "../../../../utils/helpers/dataTable";
import { dataTableCopy } from "../../../../utils/resources/dataTableCopy";

const stateExposureConfig = {
  transition: {
    type: "less-than",
    filterValue: 5,
    targetColumnKey: "columnTitle",
    targetColumnKeyValues: [
      "Maintain and Reinvest Proceeds",
      "Minimize Tax Liability",
      "Balance Tax Liability & Tracking Error",
      "Minimize Tracking Error",
    ],
  },
  cash: {
    type: "less-than",
    filterValue: 5,
    targetColumnKey: "columnTitle",
    targetColumnKeyValues: [
      "Target Portfolio [1]",
      "Sample Representative Portfolio [2]",
      "Reference Index [2]",
    ],
  },
};

const StateExposureDataTable = () => {
  const reportName = "State";
  const data = useOutputReportStore((state) => state.data);
  const types = useOutputReportStore((state) => state.types);
  const { reportType } = getOutputType(data);
  const otherStatesText = "Other States";

  // Get Unique Key
  const getUniqueKeys = (data) => {
    const uniqueKeys = data.reduce((acc, { dataKeys }) => {
      if (acc.length <= 0) {
        acc.push(...dataKeys);
      } else {
        acc.push(...dataKeys.filter((key) => !acc.includes(key)));
      }
      return acc;
    }, []);

    // Default Keys aside from States
    uniqueKeys.unshift("Cash");
    uniqueKeys.push("columnTitle");
    uniqueKeys.push(otherStatesText);

    return uniqueKeys;
  };

  const isValidForOtherState = (value, type, filterValue) => {
    // excluding data with lesser value
    // excluding data with greater value
    return (
      (type === "less-than" && value * 100 < filterValue) ||
      (type === "greater-than" && value * 100 > filterValue)
    );
  };

  const getOtherStatesSum = (data, filteredKeys) => {
    const filteredData = Object.entries(data)
      .reduce((acc, item) => {
        if (
          !filteredKeys.includes(item[0]) &&
          item[0] !== "columnTitle" &&
          item[0] !== "Total" &&
          item[0] !== "Cash" &&
          !isNaN(item[1]) &&
          item[1] > 0
        ) {
          acc.push(item[1]);
        }
        return acc;
      })
      .filter((item) => !isNaN(item));

    return filteredData.reduce((total, num) => total + num, 0);
  };

  const getCombinedData = (keys, data, currKeys, currData) => {
    return keys.reduce((acc, key, idx) => {
      if (idx === 1) {
        acc = currData;
      }
      if (currKeys.includes(key)) {
        if (data[key] > currData[key]) {
          acc[key] = data[key];
        }
      } else {
        acc[key] = data[key];
      }
      return acc;
    }, {});
  };

  const pickHighest = (obj, num) => {
    if (num > Object.keys(obj).length) {
      return false;
    }
    return Object.keys(obj)
      .sort((a, b) => obj[b] - obj[a])
      .map((key, idx) => {
        if (idx < num) {
          return { [key]: obj[key] };
        }
        return null;
      })
      .filter(Boolean)
      .reduce((acc, obj) => {
        return { ...acc, ...obj };
      }, {});
  };

  // Render State Exposure Data
  const renderStateExposureData = (data, filteredData) => {
    let filteredKeys = getUniqueKeys(filteredData);
    const stateLimitNumber = 15;

    if (filteredKeys.length - 3 > stateLimitNumber) {
      // merge datasets
      const mergedData = filteredData.reduce(
        (acc, { data }) => {
          const keys = Object.keys(data);
          if (acc.dataKeys.length <= 0) {
            acc.dataKeys = keys;
            acc.data = data;
          } else {
            const currData = acc.data;
            const currKeys = acc.dataKeys;
            // Keys
            const newKeys = acc.dataKeys.concat(keys);
            const uniqueKeys = newKeys.filter(
              (item, pos) => newKeys.indexOf(item) === pos
            );
            acc.dataKeys = uniqueKeys;

            // Data
            const combinedData = getCombinedData(
              keys,
              data,
              currKeys,
              currData
            );
            acc.data = combinedData;
          }
          return acc;
        },
        { dataKeys: [], data: [] }
      );

      // get top picks
      const highestKeys = pickHighest(mergedData.data, stateLimitNumber);
      filteredKeys = Object.keys(highestKeys);

      // Include default keys
      filteredKeys.unshift("Cash");
      filteredKeys.push("columnTitle");
      filteredKeys.push("Other States");
    }

    const rowData = data.map((item) => {
      const filteredItem = Object.keys(item)
        .filter((key) => filteredKeys.includes(key))
        .reduce((obj, key) => {
          return Object.assign(obj, {
            [key]: item[key],
          });
        }, {});

      filteredItem[otherStatesText] = getOtherStatesSum(item, filteredKeys);

      return filteredItem;
    });

    const rowKeys = filteredKeys
      .filter((value) => value !== "columnTitle")
      .map((value) => {
        return {
          label: value,
          key: value,
        };
      });

    return {
      data: rowData,
      keys: rowKeys,
    };
  };

  // Get State Exposure Data
  const getStateExposureData = ({
    data,
    type = "less-than",
    filterValue,
    targetColumnKey = "columnTitle",
    targetColumnKeyValues = ["Maintain and Reinvest Proceeds"],
  }) => {
    // filter data by target column key values
    const filteredData = data
      .filter((item) => targetColumnKeyValues.includes(item[targetColumnKey]))
      .map((item) => {
        const reduced = Object.keys(item)
          .filter(
            (key) => key !== "columnTitle" && key !== "Total" && key !== "Cash"
          )
          .reduce((obj, key) => {
            return Object.assign(
              obj,
              isValidForOtherState(item[key], type, filterValue)
                ? {}
                : { [key]: item[key] }
            );
          }, {});
        return {
          columnTitle: item["columnTitle"],
          dataKeys: Object.keys(reduced),
          data: reduced,
        };
      });

    return renderStateExposureData(data, filteredData);
  };

  stateExposureConfig[reportType]["data"] = formatDataTableColumns(
    reportName,
    data
  );
  const stateExposureData = getStateExposureData(
    stateExposureConfig[reportType]
  );

  // Note: otherStatesText format type is included to support percent value display
  types[reportName][otherStatesText] = "percent";

  const footnotes = {
    transition: (
      <>
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.existingPortfolioInformation}
        <br />
        [2]{dataTableCopy.modelAndTransitionAnalysis}
        <br />
        [3]{dataTableCopy.seeReferenceIndex}
      </>
    ),
    cash: (
      <>
        {dataTableCopy.unlessOtherwiseStated}
        <br />
        [1]{dataTableCopy.targetPortfolios}
        <br />
        [2]{dataTableCopy.seeReferenceIndex}
      </>
    ),
  };

  return (
    <DataTable
      reportType={reportType}
      variation="stylized"
      tableCaption={`${reportName} Data Table`}
      tableRowHeaderKey="State Exposure"
      tableRowKeys={stateExposureData.keys}
      tableColumns={stateExposureData.data}
      tableDataTypes={types?.[reportName]}
      formatCellDataBy="row"
      footnote={footnotes[reportType]}
      name="stateExposure"
    />
  );
};

export default StateExposureDataTable;
